import { Experience, ExperienceFilter, ExperienceQuery } from '../types/experiences'
import merge from 'lodash/merge'

import { graphQLClient } from './authService'
import experienceQueries from './graphql/experience/queries'
import { PartialPick } from '../types/type-helper'
import experienceMutations from './graphql/experience/mutations'

class ExperienceService {
  getAll = async (
    params: { [x: string]: string },
    filter?: ExperienceFilter,
    variables: any = {}
  ): Promise<Experience[]> => {
    const where: ExperienceQuery = {
      OR: [{ title_contains: params.search }, { text_contains: params.search }],
    }

    if (filter) {
      // filter for rating
      if (filter.rating.length > 0) {
        where.evaluation_in = filter.rating
      }

      // filter for status
      if (filter.status) {
        switch (filter.status) {
          case 'VERIFIED':
            where.isValidated = true

            break
          case 'AT_LOCATION':
            where.isVerified = true
            where.isValidated = false
            break
          case 'UNVERIFIED':
            where.isVerified = false
            break
          default:
            break
        }
      }

      // filter for username
      if (filter.usernames.length > 0) {
        Object.assign(where, {
          author: {
            id_in: filter.usernames.map((user) => user.id),
          },
        })
      }

      // filter for location
      if (filter.locations.length > 0) {
        Object.assign(where, {
          place: {
            id_in: filter.locations.map((place) => place.id),
          },
        })
      }

      // filter for a city
      if (filter.areas.length > 0) {
        Object.assign(where, {
          place: {
            location: {
              id_in: filter.areas.map((place) => place.location.id),
            },
          },
        })
      }

      // filter for customers
      if (filter.customers.length > 0) {
        Object.assign(where, {
          place: {
            customer: {
              id_in: filter.customers.map((customer) => customer.id),
            },
          },
        })
      }

      // filter for dates
      if (filter.date_from) {
        Object.assign(where, { createdAt_gte: filter.date_from })
      }
      if (filter.date_to) {
        Object.assign(where, { createdAt_lte: filter.date_to })
      }
    }

    const mergedVariables = merge({ where }, variables)

    const response = await graphQLClient.request(experienceQueries.getAll, mergedVariables)

    return response.experiences
  }

  getUserExperiences = async ({ id }: PartialPick<Experience, 'id'>, variables: any = {}): Promise<Experience[]> => {
    const where = {
      author: {
        id,
      },
    }

    const response = await graphQLClient.request(experienceQueries.getAll, {
      where,
      ...variables,
    })
    return response.experiences
  }

  getCustomerExperiences = async (
    { id }: PartialPick<Experience, 'id'>,
    variables: any = {}
  ): Promise<Experience[]> => {
    const where = {
      place: {
        customer: {
          id,
        },
      },
    }

    const response = await graphQLClient.request(experienceQueries.getAll, {
      where,
      ...variables,
    })
    return response.experiences
  }

  getById = async (id: string): Promise<Experience> => {
    const response = await graphQLClient.request(experienceQueries.getSingle, {
      where: {
        id,
      },
    })
    return response.experience
  }

  deleteById = async (id: string): Promise<Experience> => {
    const response = await graphQLClient.request(experienceMutations.deleteById, {
      where: {
        id_in: [id],
      },
    })

    return response.experience
  }

  replyToExperience = async (data: { id: string; text: string }): Promise<{ id: string; text: string }> => {
    const response = await graphQLClient.request(experienceMutations.replyToExperience, {
      data: {
        experience: {
          connect: {
            id: data.id,
          },
        },
        text: data.text,
      },
    })

    return response.experience
  }
}

const experienceService = new ExperienceService()

export default experienceService
